// src/pages/Login.js

import React, { useState, useRef, useEffect, useContext } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import logo from '../assets/Service-manager.png';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loginStatus, setLoginStatus] = useState('default'); 
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormHiding, setIsFormHiding] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    
    // Initialize state from localStorage so it persists
    const [showAltBackground, setShowAltBackground] = useState(() => {
        return localStorage.getItem('loginAltBg') === 'true';
    });

    const [tokenForRedirect, setTokenForRedirect] = useState(null);

    const { user, login } = useContext(AuthContext);
    const location = useLocation();
    // const navigate = useNavigate(); // ✅ Not strictly needed for the auto-redirect, but kept if used elsewhere
    const from = location.state?.from?.pathname || "/";
    
    const wrapperRef = useRef(null);
    const emailRef = useRef(null);
    const passwordRef = useRef(null);

    useEffect(() => {
        if (user) return;
        document.body.classList.add('login-page-active');
        return () => document.body.classList.remove('login-page-active');
    }, [user]);

    useEffect(() => {
        const node = wrapperRef.current;
        if (!node) return;
        const handleAnimationEnd = () => {
            if (loginStatus === 'success-wipe' && tokenForRedirect) {
                // ✅ FIX: Only call login(). The state update will trigger the re-render,
                // and the 'if (user)' block below will handle the navigation safely.
                login(tokenForRedirect); 
            } else if (loginStatus === 'error-hiding') {
                setLoginStatus('default');
                setError('');
            }
        };
        if (loginStatus === 'success-wipe' || loginStatus === 'error-hiding') {
            node.addEventListener('animationend', handleAnimationEnd, { once: true });
        }
        return () => { node.removeEventListener('animationend', handleAnimationEnd); };
    }, [loginStatus, tokenForRedirect, login]); // Removed navigate and from dependency
    
    useEffect(() => {
        let lastKey = '';
        
        const handleKeyDown = (e) => {
            if (e.shiftKey && e.altKey) {
                const key = e.key.toLowerCase();
                
                if (key === 'b') {
                    lastKey = 'b';
                } else if (key === 'g' && lastKey === 'b') {
                    // Update state AND save to localStorage
                    setShowAltBackground(prev => {
                        const newState = !prev;
                        localStorage.setItem('loginAltBg', newState);
                        return newState;
                    });
                    lastKey = ''; 
                    console.log("🎨 Background Toggled & Saved");
                } else if (key !== 'shift' && key !== 'alt') {
                    lastKey = '';
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleInitialAnimationEnd = () => setIsInitialLoad(false);

    const handleEmailKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            passwordRef.current?.focus();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);
        setError('');

        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Login failed');

            setIsFormHiding(true);
            setTimeout(() => {
                setTokenForRedirect(data.token);
                setLoginStatus('success-wipe');
            }, 250);

        } catch (err) {
            setError(err.message);
            setLoginStatus('error');
            setPassword('');
            setIsSubmitting(false);
            if (passwordRef.current) {
                passwordRef.current.focus();
            }
            setTimeout(() => {
                setLoginStatus('error-hiding');
            }, 2000);
        }
    };

    const wrapperClassName = `login-outline-wrapper ${isInitialLoad ? 'animate-in' : ''} ${loginStatus !== 'default' ? `login-outline-wrapper--${loginStatus}` : ''}`;
    const formClassName = `login-form ${isFormHiding ? 'login-form--hiding' : ''}`;

    // ✅ This block handles the actual redirection once the context has updated
    if (user) {
        return <Navigate to={from} replace />;
    }

    return (
        <div className={`login-container ${showAltBackground ? 'alt-bg' : ''}`}>
            <div
                ref={wrapperRef}
                className={wrapperClassName}
                onAnimationEnd={isInitialLoad ? handleInitialAnimationEnd : undefined}
            >
                <div className="login-box">
                    <img src={logo} alt="Ramco Service Management Logo" className="login-logo" />
                    {loginStatus !== 'success-wipe' ? (
                        <form onSubmit={handleSubmit} className={formClassName} noValidate>
                            <div className="form-group">
                                <label htmlFor="email">Email</label>
                                <input
                                    type="email" id="email" ref={emailRef}
                                    onKeyDown={handleEmailKeyDown} value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required autoFocus disabled={isSubmitting}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="password">Password</label>
                                <input
                                    type="password" id="password" ref={passwordRef}
                                    value={password} onChange={(e) => setPassword(e.target.value)}
                                    required disabled={isSubmitting}
                                />
                            </div>
                            <button
                                type="submit"
                                className={`btn btn-primary ${loginStatus === 'error' ? 'btn-error' : ''}`}
                                disabled={isSubmitting}
                            >
                                {loginStatus === 'error' ? error : isSubmitting ? 'Checking...' : 'Login'}
                            </button>
                        </form>
                    ) : (
                        <p className="login-prompt">Success! Redirecting...</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Login;